import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, saveToken, clearToken, getToken } from "@/lib/api";

const Ctx = createContext(null);

/**
 * Level-1 auth context.
 *
 * Access token lives in memory only. On page load we call `/auth/refresh` —
 * if the browser still carries the HttpOnly refresh cookie we get a new
 * access token silently; if not, `user` stays null and the SPA falls to
 * the login screen. `logout` calls the backend so the current session is
 * revoked server-side (and the cookies are wiped).
 */
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const bootstrap = useCallback(async () => {
        try {
            // Try refreshing first — this seeds the in-memory access token
            // from the refresh cookie on page reload.
            const res = await api.refreshToken();
            if (res?.access_token) {
                saveToken(res.access_token);
                // FIELD-001C · The `/auth/refresh` response returns the
                // slim login user shape (id/email/name/role/permissions
                // only). Enrich via `/auth/me` in the same tick so
                // `engineer_id` and `leads_project_ids` are present
                // BEFORE any consumer renders — otherwise Project Lead
                // authority (standalone New task, per-field enables)
                // flickers off after the auth state settles on refresh
                // and direct URL loads.
                try {
                    const enriched = await api.me();
                    setUser(enriched || res.user);
                } catch { setUser(res.user); }
                return;
            }
        } catch (_) { /* no session — that's fine */ }
        // Best-effort /me for pods where cookies were already primed by
        // something else (e.g. tests inject the access token directly).
        try {
            if (getToken()) {
                const me = await api.me();
                setUser(me);
                return;
            }
        } catch (_) { clearToken(); }
        setUser(null);
    }, []);

    useEffect(() => {
        (async () => { await bootstrap(); setLoading(false); })();
    }, [bootstrap]);

    const login = async (email, password) => {
        const res = await api.login(email.trim(), password);
        saveToken(res.access_token);
        setUser(res.user);
        // FIELD-001C · Load enriched /me so `user.engineer_id` and
        // `user.leads_project_ids` are populated for the task-panel
        // action gates and standalone "New task" affordance the same
        // tick we finish signing in. Best-effort — a failure just
        // leaves the login-shape user which is still valid.
        try {
            const enriched = await api.me();
            if (enriched) setUser(enriched);
        } catch { /* leave login-shape user */ }
        return res.user;
    };

    const register = async (email, password, name, role) => {
        const res = await api.register(email.trim(), password, name.trim(), role);
        saveToken(res.access_token);
        setUser(res.user);
        return res.user;
    };

    const logout = async () => {
        try { await api.logout(); } catch (_) { /* server may already be gone */ }
        clearToken();
        setUser(null);
    };

    const logoutAll = async () => {
        try { await api.logoutAll(); } catch (_) {}
        clearToken();
        setUser(null);
    };

    const refresh = useCallback(async () => {
        try {
            const me = await api.me();
            setUser(me);
        } catch { clearToken(); setUser(null); }
    }, []);

    // DEV-011C · Adopt the session returned by the activation endpoint
    // without a second /me round-trip. Stores the access token and
    // surfaces the user immediately so the activation page can route
    // straight into the Customer portal.
    const setAuthFromToken = useCallback(async (accessToken, userDoc) => {
        if (!accessToken) return null;
        saveToken(accessToken);
        if (userDoc) { setUser(userDoc); return userDoc; }
        try {
            const me = await api.me();
            setUser(me);
            return me;
        } catch { return null; }
    }, []);

    return (
        <Ctx.Provider value={{ user, loading, login, register, logout, logoutAll, refresh, setAuthFromToken, hasPermission: (p) => (user?.permissions || []).includes(p) }}>
            {children}
        </Ctx.Provider>
    );
}

export function useAuth() {
    const c = useContext(Ctx);
    if (!c) throw new Error("useAuth outside AuthProvider");
    return c;
}
